/**
 * Classifies a Supabase sign-up failure into the layer that actually failed.
 *
 * Sign-up can fail in layers with nothing in common — a weak password, an email
 * already in use, the confirmation email not going out, or the profile insert
 * blowing up in the database trigger. GoTrue reports the last two with the same
 * `unexpected_failure` code and only the message tells them apart, so before
 * this mapper every one of them surfaced as the same generic toast. That is how
 * a mail-delivery problem gets investigated for two days as a schema problem.
 */

export type SignUpErrorReason =
  | 'weak_password'
  | 'email_already_registered'
  | 'email_send_failure'
  | 'database_error'
  | 'unknown';

export interface SignUpErrorClassification {
  reason: SignUpErrorReason;
  /** i18n key for the message shown to the user. */
  messageKey: string;
  /** Field to focus, when the failure points at one. */
  field?: 'password';
}

interface SupabaseLikeError {
  message?: unknown;
  code?: unknown;
  status?: unknown;
}

const asError = (error: unknown): SupabaseLikeError =>
  typeof error === 'object' && error !== null ? (error as SupabaseLikeError) : {};

const messageOf = (error: SupabaseLikeError): string =>
  typeof error.message === 'string' ? error.message.toLowerCase() : '';

export const classifySignUpError = (error: unknown): SignUpErrorClassification => {
  const err = asError(error);
  const message = messageOf(err);
  const code = typeof err.code === 'string' ? err.code : '';
  const status = typeof err.status === 'number' ? err.status : undefined;

  if (code === 'weak_password' || status === 422) {
    return { reason: 'weak_password', messageKey: 'auth.step_two.password_too_weak', field: 'password' };
  }

  if (
    code === 'user_already_exists' ||
    code === 'email_exists' ||
    message.includes('already registered') ||
    message.includes('already exists')
  ) {
    return { reason: 'email_already_registered', messageKey: 'auth.email_already_registered_description' };
  }

  // GoTrue reports both mail-delivery problems and send-rate limits here.
  if (
    code === 'over_email_send_rate_limit' ||
    status === 429 ||
    message.includes('sending confirmation email') ||
    message.includes('email rate limit')
  ) {
    return { reason: 'email_send_failure', messageKey: 'auth.sign_up_email_send_failed' };
  }

  // The profile insert failed inside the on_auth_user_created trigger.
  // SPEC_DEVIATION: AUTH-20 asks for a dedicated "username taken" message when
  // the availability check and the insert race. GoTrue collapses every trigger
  // failure into `unexpected_failure` / "Database error saving new user" and
  // does not expose the violated constraint, so a username collision is not
  // distinguishable here from any other insert failure.
  // Reason: not implementable from the sign-up error alone — needs a spec
  // decision (either drop AUTH-20 or move the check into a pre-insert RPC).
  if (message.includes('database error')) {
    return { reason: 'database_error', messageKey: 'auth.sign_up_database_error' };
  }

  return { reason: 'unknown', messageKey: 'auth.failed_to_create_account' };
};

/**
 * Logs the machine-readable part of the failure. Never logs the message body,
 * the email, or any other field the user typed.
 */
export const logSignUpError = (error: unknown, reason: SignUpErrorReason): void => {
  const err = asError(error);
  console.error('[signup] failed', { reason, code: err.code, status: err.status });
};
